// src/components/MissionCard.jsx
import React, {useState, useEffect} from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
  Stack,
  LinearProgress,
  IconButton
} from '@mui/material';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate } from 'react-router-dom';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LoginReward from "../LoginReward";
import { useTranslation } from "react-i18next";
import * as Constant from "../../features/constant";

// Assuming you have a styles.css file with a .typography-global class
// This file is imported but its color styles will not be affected or overridden by these
// environment variables, unless the 'typography-global' class's color is explicitly
// removed from styles.css and applied via sx prop with an env var here.
// For now, it's untouched as per request.
import './styles.css';

const getStandardRewardDesc = (t, rewardType, amount, randomRewardConfig) => {
  if (rewardType === 'FIXED_REWARD') {
  return `${t('mission.fixedreward')} ${amount} ${t('mission.currency')}`;
  }
  if ( rewardType === 'RANDOM_REWARD') {
    return `${t('mission.randomreward')} ${randomRewardConfig?.displayAmount} ${t('mission.currency')}`;
    }
  if (rewardType === 'ACTIVITY_POINT') {
  return `${t('mission.activitypoint')} ${amount} ${t('points')}`;
  }
  };
  
  const getStandardMissionDesc = (t, mission) => {
  const descParts = [];
  
  if (mission.missionType === 'DAILY_LOGIN') {
    descParts.push(t('mission.targetlogin'));
  }
  else if (mission.missionType === 'YESTERDAY_LOSS' || mission.missionType === 'YESTERDAY_VALID_BET') {
    descParts.push(t('mission.settlementperiod'));
  }
  else if (mission.depositTarget || mission.turnoverTarget) {
    const targetValue = mission.depositTarget || mission.turnoverTarget;
    descParts.push(`${t('mission.target')} ${targetValue} ${t('mission.ormore')}`);
  }
  
  return descParts.join(' ');
  };

const MissionCard = ({ mission, onAction, isOverMinCurrentCredit }) => {
  const { t } = useTranslation();

  const [showLoginReward, setShowLoginReward] = useState(false);
  const handleLoginReward = () => {
    setShowLoginReward(true);
  };
  const navigate = useNavigate();

  // // --- Define Colors using process.env or Fallback to Original Defaults ---
  // // For Create React App, environment variables must be prefixed with REACT_APP_
  // // Adjust prefix if you're using a different bundler (e.g., VITE_ for Vite).

  // // Card Background
  // const MISSION_CARD_BACKGROUND_COLOR = process.env.REACT_APP_MISSION_CARD_BACKGROUND_COLOR || '#222222';

  // // Text Colors
  // const MISSION_CARD_TEXT_PRIMARY_COLOR = process.env.REACT_APP_MISSION_CARD_TEXT_PRIMARY_COLOR || 'white';
  // const MISSION_CARD_TEXT_SECONDARY_COLOR = process.env.REACT_APP_MISSION_CARD_TEXT_SECONDARY_COLOR || '#666';

  // // Progress Bar Colors
  // const MISSION_CARD_PROGRESS_ACCENT_COLOR = process.env.REACT_APP_MISSION_CARD_PROGRESS_ACCENT_COLOR || '#E91E63'; // Pink for progress fill
  // const MISSION_CARD_PROGRESS_TRACK_BACKGROUND_COLOR = process.env.REACT_APP_MISSION_CARD_PROGRESS_TRACK_BACKGROUND_COLOR || '#e0e0e0'; // Light grey for progress bar track

  // // Action Button Colors
  // const MISSION_CARD_ACTION_BUTTON_BACKGROUND_COLOR = process.env.REACT_APP_MISSION_CARD_ACTION_BUTTON_BACKGROUND_COLOR || '#F44336'; // Red for buttons
  // const MISSION_CARD_ACTION_BUTTON_TEXT_COLOR = process.env.REACT_APP_MISSION_CARD_ACTION_BUTTON_TEXT_COLOR || '#fff'; // White text for red button
  // const MISSION_CARD_ACTION_BUTTON_HOVER_BACKGROUND_COLOR = process.env.REACT_APP_MISSION_CARD_ACTION_BUTTON_HOVER_BACKGROUND_COLOR || '#E53935'; // Slightly darker red on hover
  // const MISSION_CARD_ACTION_BUTTON_DISABLED_BACKGROUND_COLOR = process.env.REACT_APP_MISSION_CARD_ACTION_BUTTON_DISABLED_BACKGROUND_COLOR || '#bdbdbd'; // Grey for disabled buttons
  // const MISSION_CARD_ACTION_BUTTON_DISABLED_TEXT_COLOR = process.env.REACT_APP_MISSION_CARD_ACTION_BUTTON_DISABLED_TEXT_COLOR || '#666'; // Grey for disabled button text

  // // Reward Icon & Text Colors
  // const MISSION_CARD_REWARD_ICON_FIXED_RANDOM_COLOR = process.env.REACT_APP_MISSION_CARD_REWARD_ICON_FIXED_RANDOM_COLOR || '#FFD700'; // Pink reward icon
  // const MISSION_CARD_REWARD_TEXT_FIXED_RANDOM_COLOR = process.env.REACT_APP_MISSION_CARD_REWARD_TEXT_FIXED_RANDOM_COLOR || '#FFD700'; // Pink reward text
  // const MISSION_CARD_REWARD_ICON_POINTS_COLOR = process.env.REACT_APP_MISSION_CARD_REWARD_ICON_POINTS_COLOR || '#FFD700'; // Gold for activity points icon
  // const MISSION_CARD_REWARD_TEXT_POINTS_COLOR = process.env.REACT_APP_MISSION_CARD_REWARD_TEXT_POINTS_COLOR || '#FFD700'; // Gold for activity points text

  // // Claimed Badge Colors
  // const MISSION_CARD_CLAIMED_BADGE_BACKGROUND_COLOR = process.env.REACT_APP_MISSION_CARD_CLAIMED_BADGE_BACKGROUND_COLOR || '#FFEB3B'; // Yellow for CLAIMED badge background
  // const MISSION_CARD_CLAIMED_BADGE_TEXT_COLOR = process.env.REACT_APP_MISSION_CARD_CLAIMED_BADGE_TEXT_COLOR || '#333'; // Dark text for CLAIMED badge
  const darkDefaults = {
    MISSION_CARD_BACKGROUND_COLOR: '#222222',
    MISSION_CARD_TEXT_PRIMARY_COLOR: 'white',
    MISSION_CARD_TEXT_SECONDARY_COLOR: '#D3D3D3',
    MISSION_CARD_PROGRESS_BACKGROUND_COLOR: '#e0e0e0',
    MISSION_CARD_PROGRESS_ACCENT_COLOR: '#E91E63',
    MISSION_CARD_PROGRESS_TRACK_BACKGROUND_COLOR: '#e0e0e0',
    MISSION_CARD_ACTION_BUTTON_BACKGROUND_COLOR: '#F44336',
    MISSION_CARD_ACTION_BUTTON_TEXT_COLOR: '#fff',
    MISSION_CARD_ACTION_BUTTON_HOVER_BACKGROUND_COLOR: '#E53935',
    MISSION_CARD_ACTION_BUTTON_DISABLED_BACKGROUND_COLOR: '#bdbdbd',
    MISSION_CARD_ACTION_BUTTON_DISABLED_TEXT_COLOR: '#666',
    MISSION_CARD_REWARD_ICON_FIXED_RANDOM_COLOR: '#FFD700',
    MISSION_CARD_REWARD_TEXT_FIXED_RANDOM_COLOR: '#FFD700',
    MISSION_CARD_REWARD_ICON_POINTS_COLOR: '#FFD700',
    MISSION_CARD_REWARD_TEXT_POINTS_COLOR: '#FFD700',
    MISSION_CARD_CLAIMED_BADGE_BACKGROUND_COLOR: '#FFEB3B',
    MISSION_CARD_CLAIMED_BADGE_TEXT_COLOR: '#333',
  };
  
  // Light mode defaults
  const lightDefaults = {
    MISSION_CARD_BACKGROUND_COLOR: '#ffffff',
    MISSION_CARD_TEXT_PRIMARY_COLOR: '#222',
    MISSION_CARD_TEXT_SECONDARY_COLOR: '#555',
    MISSION_CARD_PROGRESS_BACKGROUND_COLOR: '#cccccc',
    MISSION_CARD_PROGRESS_ACCENT_COLOR: '#2196F3',
    MISSION_CARD_PROGRESS_TRACK_BACKGROUND_COLOR: '#cccccc',
    MISSION_CARD_ACTION_BUTTON_BACKGROUND_COLOR: '#1976d2',
    MISSION_CARD_ACTION_BUTTON_TEXT_COLOR: '#fff',
    MISSION_CARD_ACTION_BUTTON_HOVER_BACKGROUND_COLOR: '#1565c0',
    MISSION_CARD_ACTION_BUTTON_DISABLED_BACKGROUND_COLOR: '#e0e0e0',
    MISSION_CARD_ACTION_BUTTON_DISABLED_TEXT_COLOR: '#999',
    MISSION_CARD_REWARD_ICON_FIXED_RANDOM_COLOR: '#FFA000',
    MISSION_CARD_REWARD_TEXT_FIXED_RANDOM_COLOR: '#FFA000',
    MISSION_CARD_REWARD_ICON_POINTS_COLOR: '#4CAF50',
    MISSION_CARD_REWARD_TEXT_POINTS_COLOR: '#4CAF50',
    MISSION_CARD_CLAIMED_BADGE_BACKGROUND_COLOR: '#FFEB3B',
    MISSION_CARD_CLAIMED_BADGE_TEXT_COLOR: '#333',
  };
  
  // Toggle (can be a state or just read from env)
  let useLightMode = process.env.REACT_APP_MISSION_CARD_LIGHTMODE || false; // <--- flip this with a toggle button in component
  
  const defaults = useLightMode ? lightDefaults : darkDefaults;
  
  // Now define constants based on chosen defaults
  const MISSION_CARD_BACKGROUND_COLOR =
    process.env.REACT_APP_MISSION_CARD_BACKGROUND_COLOR || defaults.MISSION_CARD_BACKGROUND_COLOR;
  
  const MISSION_CARD_TEXT_PRIMARY_COLOR =
    process.env.REACT_APP_MISSION_CARD_TEXT_PRIMARY_COLOR || defaults.MISSION_CARD_TEXT_PRIMARY_COLOR;
  
  const MISSION_CARD_TEXT_SECONDARY_COLOR =
    process.env.REACT_APP_MISSION_CARD_TEXT_SECONDARY_COLOR || defaults.MISSION_CARD_TEXT_SECONDARY_COLOR;
  
  const MISSION_CARD_PROGRESS_BACKGROUND_COLOR =
    process.env.REACT_APP_MISSION_CARD_PROGRESS_BACKGROUND_COLOR || defaults.MISSION_CARD_PROGRESS_BACKGROUND_COLOR;

  const MISSION_CARD_PROGRESS_ACCENT_COLOR =
    process.env.REACT_APP_MISSION_CARD_PROGRESS_ACCENT_COLOR || defaults.MISSION_CARD_PROGRESS_ACCENT_COLOR;
  
  const MISSION_CARD_PROGRESS_TRACK_BACKGROUND_COLOR =
    process.env.REACT_APP_MISSION_CARD_PROGRESS_TRACK_BACKGROUND_COLOR || defaults.MISSION_CARD_PROGRESS_TRACK_BACKGROUND_COLOR;
  
  const MISSION_CARD_ACTION_BUTTON_BACKGROUND_COLOR =
    process.env.REACT_APP_MISSION_CARD_ACTION_BUTTON_BACKGROUND_COLOR || defaults.MISSION_CARD_ACTION_BUTTON_BACKGROUND_COLOR;
  
  const MISSION_CARD_ACTION_BUTTON_TEXT_COLOR =
    process.env.REACT_APP_MISSION_CARD_ACTION_BUTTON_TEXT_COLOR || defaults.MISSION_CARD_ACTION_BUTTON_TEXT_COLOR;
  
  const MISSION_CARD_ACTION_BUTTON_HOVER_BACKGROUND_COLOR =
    process.env.REACT_APP_MISSION_CARD_ACTION_BUTTON_HOVER_BACKGROUND_COLOR || defaults.MISSION_CARD_ACTION_BUTTON_HOVER_BACKGROUND_COLOR;
  
  const MISSION_CARD_ACTION_BUTTON_DISABLED_BACKGROUND_COLOR =
    process.env.REACT_APP_MISSION_CARD_ACTION_BUTTON_DISABLED_BACKGROUND_COLOR ||
    defaults.MISSION_CARD_ACTION_BUTTON_DISABLED_BACKGROUND_COLOR;
  
  const MISSION_CARD_ACTION_BUTTON_DISABLED_TEXT_COLOR =
    process.env.REACT_APP_MISSION_CARD_ACTION_BUTTON_DISABLED_TEXT_COLOR ||
    defaults.MISSION_CARD_ACTION_BUTTON_DISABLED_TEXT_COLOR;
  
  const MISSION_CARD_REWARD_ICON_FIXED_RANDOM_COLOR =
    process.env.REACT_APP_MISSION_CARD_REWARD_ICON_FIXED_RANDOM_COLOR || defaults.MISSION_CARD_REWARD_ICON_FIXED_RANDOM_COLOR;
  
  const MISSION_CARD_REWARD_TEXT_FIXED_RANDOM_COLOR =
    process.env.REACT_APP_MISSION_CARD_REWARD_TEXT_FIXED_RANDOM_COLOR || defaults.MISSION_CARD_REWARD_TEXT_FIXED_RANDOM_COLOR;
  
  const MISSION_CARD_REWARD_ICON_POINTS_COLOR =
    process.env.REACT_APP_MISSION_CARD_REWARD_ICON_POINTS_COLOR || defaults.MISSION_CARD_REWARD_ICON_POINTS_COLOR;
  
  const MISSION_CARD_REWARD_TEXT_POINTS_COLOR =
    process.env.REACT_APP_MISSION_CARD_REWARD_TEXT_POINTS_COLOR || defaults.MISSION_CARD_REWARD_TEXT_POINTS_COLOR;
  
  const MISSION_CARD_CLAIMED_BADGE_BACKGROUND_COLOR =
    process.env.REACT_APP_MISSION_CARD_CLAIMED_BADGE_BACKGROUND_COLOR || defaults.MISSION_CARD_CLAIMED_BADGE_BACKGROUND_COLOR;
  
  const MISSION_CARD_CLAIMED_BADGE_TEXT_COLOR =
    process.env.REACT_APP_MISSION_CARD_CLAIMED_BADGE_TEXT_COLOR || defaults.MISSION_CARD_CLAIMED_BADGE_TEXT_COLOR;

  const {
    missionName, missionType, missionMode,
    target, amount, progress,
    rewardType, rewardDesc,
    completed, claimed, desc,
    hasDeposit, hasTurnover, // General flags for single missions
    randomRewardConfig,

    depositTarget, depositAmount, depositProgress,
    depositCompleted, depositClaimed,

    turnoverTarget, turnoverAmount, turnoverProgress,
    turnoverCompleted, turnoverClaimed,
  } = mission;

  const standardizedRewardDesc = getStandardRewardDesc(t, rewardType, amount, randomRewardConfig);
  const standardizedMissionDesc = getStandardMissionDesc(t, mission);
  const standardizedMissionName = t(`mission.${mission.missionType}.name`);

  const isDailyLogin = missionType === 'DAILY_LOGIN';
  const isCombinedMission = missionType === 'ACCUMULATED_DEPOSIT_WITH_TURNOVER';
  const hasAnySingleProgress = !isDailyLogin && !isCombinedMission && (depositTarget !== undefined || turnoverTarget !== undefined )
  && (depositTarget !== null || turnoverTarget !== null ) && (parseFloat(depositTarget) > 0 || parseFloat(turnoverTarget) > 0);

  // --- Helper to render a Circular Progress Bar (percentage inside) ---
  const renderCircularProgressBar = (currentAmount, currentTarget, currentProgress, size = 50) => {
    if (
      currentTarget === undefined ||
      currentTarget === null ||
      parseFloat(currentTarget) === 0
    ) {
      return null;
    }
  
    let calculatedProgress;
  
    if (currentProgress !== undefined && currentProgress !== null) {
      const parsedProgress = parseFloat(currentProgress);
      // if (parsedProgress <= 1) {
      //   // Already a fraction (e.g., 0.8 for 80%)
      //   calculatedProgress = parsedProgress;
      // } else {
      //   // Absolute progress (e.g., 400 out of 500)
        calculatedProgress = parsedProgress / parseFloat(currentTarget || 1);
      // }
    } 
  
    const displayPercentage = Math.min(
      100,
      Math.round(calculatedProgress * 100)
    );
  

    return (
      <Box
        sx={{
          position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: size + 10, height: size + 10, // Slightly larger box than circle itself for spacing
          flexShrink: 0,
        }}
      >
        <CircularProgress
      variant="determinate"
      value={100}
      size={size}
      thickness={4}
      sx={{ 
        color: MISSION_CARD_PROGRESS_BACKGROUND_COLOR,
        // color: '#ffffff', // transparent/soft ring
        position: 'absolute',
      }}
    />
    
        <CircularProgress
          variant="determinate"
          value={displayPercentage}
          size={size}
          thickness={4}
          sx={{ color: MISSION_CARD_PROGRESS_ACCENT_COLOR }} // Use env var
        />
        
        <Box
          sx={{
            top: 0, left: 0, bottom: 0, right: 0,
            position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Typography className="typography-global" variant="caption" component="div" sx={{ fontSize: '0.7rem', color: MISSION_CARD_TEXT_PRIMARY_COLOR }}>
            {`${displayPercentage}%`}
          </Typography>
        </Box>
      </Box>
    );
  };

  // --- Helper to render a Horizontal Progress Bar ---
  const renderHorizontalProgressBar = (currentAmount, currentTarget, currentProgress, width = '100px') => {
    if (
      currentTarget === undefined ||
      currentTarget === null ||
      parseFloat(currentTarget) === 0
    ) {
      return null;
    }
  
    let calculatedProgress;
  
    if (currentProgress !== undefined && currentProgress !== null) {
      const parsedProgress = parseFloat(currentProgress);
      // if (parsedProgress <= 1) {
      //   // Already a fraction (e.g., 0.8 for 80%)
      //   calculatedProgress = parsedProgress;
      // } else {
      //   // Absolute progress (e.g., 400 out of 500)
        calculatedProgress = parsedProgress / parseFloat(currentTarget || 1);
      // }
    } 
    const displayPercentage = Math.min(
      100,
      Math.round(calculatedProgress * 100)
    );

    return (
      <Box sx={{ position: 'relative', width, flexShrink: 0 }}>
        <LinearProgress
          variant="determinate"
          value={displayPercentage}
          sx={{
            height: 6, borderRadius: 3,
            backgroundColor: MISSION_CARD_PROGRESS_TRACK_BACKGROUND_COLOR, // Use env var
            '& .MuiLinearProgress-bar': { backgroundColor: MISSION_CARD_PROGRESS_ACCENT_COLOR }, // Use env var
            width: width, // Fixed width
            flexShrink: 0,
          }}
        />

        {displayPercentage > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: `calc(${displayPercentage}% - 4.8px)`,
                transform: 'translate(-50%, -50%)',
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: 'black',
                border: `2px solid ${MISSION_CARD_PROGRESS_ACCENT_COLOR}`,
              }}
            />
          )}
      </Box>
    );
  };

  // --- Helper to render an Action Button ---
  const renderActionButton = (actionType, buttonText, isDisabled, routePath = null, customStyle = {}) => {
    const shouldDisable = isDisabled || isOverMinCurrentCredit;  

    const handleClick = () => {
      if (routePath === Constant.getPath("/profileWallet")) {
        navigate(routePath, {
          state: {
            screen: 'deposit'
          }
        });
      }
      else if (routePath) {
        navigate(routePath);
      }
      else {
        onAction(mission, actionType);
      }
    };

    return (
      <Button
        variant="contained"
        onClick={handleClick}
        disabled={shouldDisable}
        sx={{
          minWidth: 100, height: 30, // Fixed height for consistency
          fontSize: '0.75rem', py: 0.7,
          backgroundColor: MISSION_CARD_ACTION_BUTTON_BACKGROUND_COLOR, // Use env var
          color: MISSION_CARD_ACTION_BUTTON_TEXT_COLOR, fontWeight: 'bold', // Use env var
          '&:hover': { backgroundColor: MISSION_CARD_ACTION_BUTTON_HOVER_BACKGROUND_COLOR }, // Use env var
          '&.Mui-disabled': { backgroundColor: MISSION_CARD_ACTION_BUTTON_DISABLED_BACKGROUND_COLOR, color: MISSION_CARD_ACTION_BUTTON_DISABLED_TEXT_COLOR }, // Use env vars
          ...customStyle,
        }}
      >
        {buttonText}
      </Button>
    );
  };

  // Logic for the "CLAIMED" badge (top-right overlay)
  const showMainClaimedBadge = (claimed && !isCombinedMission) ||
                               (isCombinedMission && depositClaimed && turnoverClaimed);
  const canClaimCombined = isCombinedMission && depositCompleted && turnoverCompleted && !claimed;


  // --- Consolidated Helper for rendering a progress/action row (single or combined part) ---
  const renderProgressAndActionButtonRow = (
    type, // 'deposit', 'turnover', or 'claim' for single missions
    currentAmount, currentTarget, currentProgress,
    label = null, // "Deposit" or "Turnover" for combined, null for single mission
    actionFlag, // hasDeposit or hasTurnover for routing, or true for claim button
    isCompleted, isClaimed,
    buttonRoutePath, missionType
  ) => {
    const showProgressElements = currentTarget !== undefined && currentTarget !== null && parseFloat(currentTarget) > 0;
    const actionButtonLabel = type === 'deposit' ? t('mission.depositbutton') : (type === 'turnover' ? t('mission.playnowbutton') : t('mission.claimbutton'));

    let actionButtonElement = null;
    if (isCompleted && !isClaimed) { // Ready to claim (for single mission, or overall combined mission claim)
      actionButtonElement = renderActionButton('claim', t('mission.claimbutton'), false);
    } else if (actionFlag && !isCompleted) { // Needs deposit/play action (for combined or single)
      actionButtonElement = renderActionButton(type, actionButtonLabel, false, buttonRoutePath);
    } else if (isClaimed) { // Already claimed (for single mission or combined part)
      actionButtonElement = <Button variant="outlined" disabled sx={{ minWidth: 100, height: 30, fontSize: '0.75rem', py: 0.7, backgroundColor: MISSION_CARD_ACTION_BUTTON_DISABLED_BACKGROUND_COLOR, color: MISSION_CARD_ACTION_BUTTON_DISABLED_TEXT_COLOR }}>{t('mission.claimedbutton')}</Button>;
    }

    if (!showProgressElements && !label && !actionButtonElement) {
        return null; // Don't render if there's no content for this row/block
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center', // Vertically center items in the row
          flexWrap: 'nowrap', // Force items onto a single line
          width: '100%',
          overflowX: 'auto', // Allow horizontal scrolling if necessary
          py: 0.5,
          gap: { xs: 1, sm: 2 }, // Gap between main sections
          justifyContent: 'space-between', // Distribute items horizontally
        }}
      >
        {/* Leftmost: Circular Progress & Optional Label */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, minWidth: '100px' }}>
          {showProgressElements && renderCircularProgressBar(currentAmount, currentTarget, currentProgress, 50)}
          {label && ( // Label like "Deposit" or "Turnover"
            <Typography className="typography-global" variant="body2" sx={{ fontWeight: 'medium', color: MISSION_CARD_TEXT_PRIMARY_COLOR }}>
              {label}
            </Typography>
          )}
        </Box>


        {/* Rightmost: Action Button */}
        <Box sx={{ flexShrink: 0, ml: { xs: 1, sm: 2 } }}>
        {showProgressElements && renderHorizontalProgressBar(currentAmount, currentTarget, currentProgress, '100px')}
          {showProgressElements && (
            <Typography className="typography-global" variant="body2" sx={{ fontSize: '0.75rem', color: MISSION_CARD_TEXT_SECONDARY_COLOR, mt: 0.5 }}>
              {`${currentProgress || 0}/${currentTarget}` + " " + t('mission.currency')}
            </Typography>
          )}
          {actionButtonElement}
        </Box>
      </Box>
    );
  };

  // const renderProgressAndActionButtonRowForCombined = (
    
  //   type, // 'deposit', 'turnover', or 'claim' for single missions
  //   currentAmount, currentTarget, currentProgress,
  //   label = null, // "Deposit" or "Turnover" for combined, null for single mission
  //   actionFlag, // hasDeposit or hasTurnover for routing, or true for claim button
  //   isCompleted, isClaimed,
  //   buttonRoutePath
  // ) => {
  //   const showProgressElements = currentTarget !== undefined && currentTarget !== null && parseFloat(currentTarget) > 0;
  //   const actionButtonLabel = type === 'deposit' ? 'DEPOSIT' : (type === 'turnover' ? 'PLAY NOW' : 'CLAIM');

  //   let actionButtonElement = null;
  //   if (isCompleted && !isClaimed) { // Ready to claim (for single mission, or overall combined mission claim)
  //     actionButtonElement = renderActionButton(type, actionButtonLabel, true); // isDisabled is true for combined parts if completed but not overall claimed
  //   } else if (actionFlag && !isCompleted) { // Needs deposit/play action (for combined or single)
  //     actionButtonElement = renderActionButton(type, actionButtonLabel, false, buttonRoutePath);
  //   } else if (isClaimed) { // Already claimed (for single mission or combined part)
  //     actionButtonElement = <Button variant="outlined" disabled sx={{ minWidth: 100, height: 30, fontSize: '0.75rem', py: 0.7, backgroundColor: MISSION_CARD_ACTION_BUTTON_DISABLED_BACKGROUND_COLOR, color: MISSION_CARD_ACTION_BUTTON_DISABLED_TEXT_COLOR }}>CLAIMED</Button>;
  //   }

  //   if (!showProgressElements && !label && !actionButtonElement) {
  //       return null; // Don't render if there's no content for this row/block
  //   }

  //   return (
  //     <Box
  //       sx={{
  //         display: 'flex',
  //         alignItems: 'center', // Vertically center items in the row
  //         flexWrap: 'nowrap', // Force items onto a single line
  //         width: '100%',
  //         overflowX: 'auto', // Allow horizontal scrolling if necessary
  //         py: 0.5,
  //         gap: { xs: 1, sm: 2 }, // Gap between main sections
  //         justifyContent: 'space-between', // Distribute items horizontally
  //       }}
  //     >
  //       {/* Leftmost: Circular Progress & Optional Label */}
  //       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, minWidth: '100px' }}>
  //         {showProgressElements && renderCircularProgressBar(currentAmount, currentTarget, currentProgress, 50)}
  //         {label && ( // Label like "Deposit" or "Turnover"
  //           <Typography className="typography-global" variant="body2" sx={{ fontWeight: 'medium', color: MISSION_CARD_TEXT_PRIMARY_COLOR }}>
  //             {label}
  //           </Typography>
  //         )}
  //       </Box>


  //       {/* Rightmost: Action Button */}
  //       <Box sx={{ flexShrink: 0, ml: { xs: 1, sm: 2 } }}>
  //       {showProgressElements && renderHorizontalProgressBar(currentAmount, currentTarget, currentProgress, '100px')}
  //         {showProgressElements && (
  //           <Typography className="typography-global" variant="body2" sx={{ fontSize: '0.75rem', color: MISSION_CARD_TEXT_SECONDARY_COLOR, mt: 0.5 }}>
  //             {`${currentProgress || 0}/${currentTarget} PHP`}
  //           </Typography>
  //         )}
  //         {actionButtonElement}
  //       </Box>
  //     </Box>
  //   );
  // };

  return (
    <>      
    <LoginReward loginRewardOpen={showLoginReward} setLoginRewardOpen={setShowLoginReward} /> 
    <Card
      sx={{
        mb: 2, borderRadius: 2, boxShadow: 1,
        position: 'relative', overflow: 'hidden',
        backgroundColor: MISSION_CARD_BACKGROUND_COLOR, // Use env var
      }}
    >
      {/* "CLAIMED" Badge Overlay */}
      {showMainClaimedBadge && (
        <Box
          sx={{
            position: 'absolute', top: 15, right: -30,
            transform: 'rotate(45deg)',
            backgroundColor: MISSION_CARD_CLAIMED_BADGE_BACKGROUND_COLOR, color: MISSION_CARD_CLAIMED_BADGE_TEXT_COLOR, // Use env vars
            px: 4, py: 0.5, fontWeight: 'bold', fontSize: '0.8rem', zIndex: 1,
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)', textTransform: 'uppercase',
            display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '120px',
          }}
        >
          CLAIMED
          <Box sx={{ position: 'absolute', top: -10, left: -10, width: 20, height: 20, transform: 'rotate(-45deg)', opacity: 0.2 }}>
            <StarIcon fontSize="small" /> <StarIcon fontSize="small" /> <StarIcon fontSize="small" />
          </Box>
        </Box>
      )}

      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 }, color: MISSION_CARD_TEXT_PRIMARY_COLOR }}> {/* Use env var */}
        {/* Top section: Mission name, general description, reward */}
        <Box>
          <Typography className="typography-global"  component="div" sx={{ fontWeight: 'bold', color: MISSION_CARD_TEXT_PRIMARY_COLOR }}>
            {standardizedMissionName}
          </Typography>
          <Typography className="typography-global" variant="caption" sx={{ mt: 0.5, color: MISSION_CARD_TEXT_SECONDARY_COLOR }}> {/* Use env var */}
            {standardizedMissionDesc}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
            {rewardType === 'FIXED_REWARD' && (
              <>
                <CurrencyExchangeIcon fontSize="small" sx={{ color: MISSION_CARD_REWARD_ICON_FIXED_RANDOM_COLOR }}/> {/* Use env var */}
                <Typography className="typography-global" variant="caption" sx={{ fontWeight: 'medium', color: MISSION_CARD_REWARD_TEXT_FIXED_RANDOM_COLOR }}> {/* Use env var */}
                  {standardizedRewardDesc}
                </Typography>
              </>
            )}
            {rewardType === 'ACTIVITY_POINT' && (
              <>
                <StarIcon fontSize="small" sx={{ color: MISSION_CARD_REWARD_ICON_POINTS_COLOR }} />
                <Typography className="typography-global" variant="caption" sx={{ fontWeight: 'medium', color: MISSION_CARD_REWARD_TEXT_POINTS_COLOR }}>
                  {standardizedRewardDesc}
                </Typography>
              </>
            )}
            {rewardType === 'RANDOM_REWARD' && (
              <>
                <CurrencyExchangeIcon fontSize="small" sx={{ color: MISSION_CARD_REWARD_ICON_FIXED_RANDOM_COLOR }} />
                <Typography className="typography-global" variant="caption" sx={{ fontWeight: 'medium', color: MISSION_CARD_REWARD_TEXT_FIXED_RANDOM_COLOR }}>
                  {standardizedRewardDesc}
                </Typography>
              </>
            )}
          </Stack>
        </Box>

        {/* Dynamic content area below the main description */}
        {isDailyLogin ? (
          // Daily Login specific layout (one CLAIM button, or one CLAIMED button)
            <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'flex-end', alignItems: 'center' }}>
            <IconButton onClick={() => handleLoginReward(true)}><CalendarTodayIcon/></IconButton>  
            {!claimed ? (
              renderActionButton('claimDaily', t('mission.claimbutton'), false)
            ) : (
              <Button variant="outlined" disabled sx={{ minWidth: 100, height: 30, fontSize: '0.75rem', py: 0.7, backgroundColor: MISSION_CARD_ACTION_BUTTON_DISABLED_BACKGROUND_COLOR, color: MISSION_CARD_ACTION_BUTTON_DISABLED_TEXT_COLOR }}>CLAIMED</Button>
            )}
          </Stack>
        ) : 
        // isCombinedMission ? (
        //   // Combined mission layout (Deposit and Turnover rows)
        //   <Stack spacing={2} sx={{ width: '100%' }}>
        //     {renderProgressAndActionButtonRowForCombined('deposit', depositAmount, depositTarget, depositProgress, 'Deposit', hasDeposit, depositCompleted, depositClaimed, '/deposit')}
        //     {renderProgressAndActionButtonRowForCombined('turnover', turnoverAmount, turnoverTarget, turnoverProgress, 'Turnover', hasTurnover, turnoverCompleted, turnoverClaimed, '/games')}

        //     {canClaimCombined && (
        //       <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 1 }}>
        //         {renderActionButton('claimCombined', 'CLAIM', false)}
        //       </Box>
        //     )}
        //   </Stack>
        // ) : hasAnySingleProgress ? 
        (
          // Single mission with progress (e.g., Daily Deposit, Invite Friends)
          <Stack spacing={2} sx={{ width: '100%' }}>



          {depositTarget ? renderProgressAndActionButtonRow(
            'deposit' , // Action type
            amount, depositTarget, depositProgress,
            null, // No specific label for single missions
            hasDeposit || hasTurnover || (completed && !claimed), // Action flag
            completed, claimed,
            hasDeposit ? Constant.getPath("/profileWallet") : (hasTurnover ? Constant.getPath("/profileWallet") : null) // Button route
          ) : null}
            {turnoverTarget ? renderProgressAndActionButtonRow(
            'turnover', // Action type
            amount, turnoverTarget, turnoverProgress,
            null, // No specific label for single missions
            hasDeposit || hasTurnover || (completed && !claimed), // Action flag
            completed, claimed,
            hasDeposit ? '/deposit' : (hasTurnover ? Constant.getPath("/home") : null) // Button route
          ): null}

          {!depositTarget && !turnoverTarget && !hasDeposit && !hasTurnover ? renderProgressAndActionButtonRow(
            'claim' , // Action type
            amount, depositTarget, depositProgress,
            null, // No specific label for single missions
            hasDeposit || hasTurnover || (completed && !claimed), // Action flag
            completed, claimed,
            hasDeposit ? Constant.getPath("/profileWallet") : (hasTurnover ? Constant.getPath("/profileWallet") : null) // Button route
          ) : null}
          </Stack>
        ) 
        // : null /* Fallback for missions without progress or specific actions */ 
        }
      </CardContent>
    </Card>
    </>
  );
};

export default MissionCard;